/**
 * Tab de Proveedores en Finanzas.
 *
 * Lista todas las empresas marcadas como `isSupplier=true` con el total
 * gastado en cada una agregado de la tabla `transactions`. Permite ver
 * gráficas de gasto por proveedor en el periodo seleccionado.
 *
 * Ligar un gasto a un proveedor se hace desde el formulario de
 * transacciones (CompanyPicker existente). Marcar/desmarcar una empresa
 * como proveedor se hace desde su modal de detalle aquí.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, Search, Pencil, ExternalLink } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart as RPieChart,
  Pie,
  Legend,
} from "recharts";
import clsx from "clsx";
import { api } from "@/lib/api";

interface Company {
  id: string;
  name: string;
  industry: string | null;
  status: "active" | "prospect" | "past" | "lost";
  isInternal: boolean;
  isSupplier: boolean;
  website: string | null;
  notes: string | null;
}

interface SupplierTxSplit {
  id: string;
  companyId: string | null;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
}

interface SupplierTx {
  id: string;
  date: string;
  kind: "ingreso" | "gasto";
  subtotalCents: number;
  totalCents: number;
  ivaCents: number;
  companyId: string | null;
  description: string | null;
  counterparty: string | null;
  splits?: SupplierTxSplit[];
}

const fmtMx = (v: number) => v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });

/**
 * Paleta para gráficas por proveedor. Color estable por nombre — usa el
 * hash del string para indexar, así un proveedor siempre cae en el mismo
 * color independientemente del orden de la lista.
 */
const SUPPLIER_PALETTE = [
  "oklch(0.65 0.2 25)", // rojo
  "oklch(0.72 0.18 60)", // naranja
  "oklch(0.7 0.18 100)", // amarillo-verde
  "oklch(0.78 0.16 145)", // verde
  "oklch(0.78 0.16 180)", // teal
  "oklch(0.62 0.18 220)", // azul
  "oklch(0.65 0.2 270)", // índigo
  "oklch(0.65 0.2 320)", // púrpura
  "oklch(0.62 0.2 0)", // pink
  "oklch(0.55 0.15 30)", // marrón
];

function supplierColor(name: string, fallbackIndex = 0): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const idx = name.length === 0 ? fallbackIndex : h % SUPPLIER_PALETTE.length;
  return SUPPLIER_PALETTE[idx] ?? SUPPLIER_PALETTE[0]!;
}

export function SuppliersTab({ period }: { period: string }) {
  const [y, m] = period.split("-").map(Number);
  const lastDay = new Date(y!, m!, 0).getDate();
  const fromDate = `${period}-01`;
  const toDate = `${period}-${String(lastDay).padStart(2, "0")}`;

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Company | null>(null);
  const [creating, setCreating] = useState(false);
  // Compartido vía localStorage con las otras tabs.
  const [expandSplits, setExpandSplits] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("axorial.tx.expandSplits") === "1";
  });

  const suppliers = useQuery<{ companies: Company[] }>({
    queryKey: ["companies", "suppliers"],
    queryFn: () => api.get("/companies?isSupplier=1"),
  });

  // Todos los gastos del mes para agregar por proveedor.
  const monthGastos = useQuery<{ transactions: SupplierTx[] }>({
    queryKey: ["finance", "suppliers-month", period],
    queryFn: () => api.get(`/finance/transactions?from=${fromDate}&to=${toDate}&kind=gasto`),
  });

  const supplierList = suppliers.data?.companies ?? [];
  const txList = monthGastos.data?.transactions ?? [];

  const filtered = supplierList.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));

  // Agregar gastos por companyId del mes. Si expandSplits=ON y la tx tiene
  // sub-cuentas, atribuimos cada sub-cuenta a su propio companyId (con
  // fallback al companyId del padre).
  const totalsByCompany = useMemo(() => {
    const map = new Map<string, { totalCents: number; count: number }>();
    for (const tx of txList) {
      if (expandSplits && tx.splits && tx.splits.length > 0) {
        for (const s of tx.splits) {
          const cid = s.companyId ?? tx.companyId;
          if (!cid) continue;
          const cur = map.get(cid) ?? { totalCents: 0, count: 0 };
          cur.totalCents += s.totalCents;
          cur.count += 1;
          map.set(cid, cur);
        }
      } else {
        if (!tx.companyId) continue;
        const cur = map.get(tx.companyId) ?? { totalCents: 0, count: 0 };
        cur.totalCents += tx.totalCents;
        cur.count += 1;
        map.set(tx.companyId, cur);
      }
    }
    return map;
  }, [txList, expandSplits]);

  // Top proveedores para gráfica de barras.
  const topChart = useMemo(() => {
    const items = supplierList
      .map((s) => ({
        name: s.name,
        total: (totalsByCompany.get(s.id)?.totalCents ?? 0) / 100,
      }))
      .filter((s) => s.total > 0)
      .toSorted((a, b) => b.total - a.total)
      .slice(0, 10);
    return items;
  }, [supplierList, totalsByCompany]);

  // Distribución completa para pie. Top 6 + "Otros" agrupando la cola para
  // mantener el pie legible sin perder el total real del periodo.
  const distributionChart = useMemo(() => {
    const all = supplierList
      .map((s) => ({
        name: s.name,
        total: (totalsByCompany.get(s.id)?.totalCents ?? 0) / 100,
      }))
      .filter((s) => s.total > 0)
      .toSorted((a, b) => b.total - a.total);
    if (all.length <= 7) return all;
    const top = all.slice(0, 6);
    const restTotal = all.slice(6).reduce((sum, s) => sum + s.total, 0);
    return [...top, { name: "Otros", total: restTotal }];
  }, [supplierList, totalsByCompany]);

  // Gastos sin proveedor en el periodo (oportunidad de etiquetar).
  const unTagged = useMemo(() => {
    return txList.filter((tx) => !tx.companyId);
  }, [txList]);
  const unTaggedTotal = unTagged.reduce((s, tx) => s + tx.totalCents, 0) / 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg">Proveedores</h2>
          <p className="text-sm text-base-content/60">
            Gastos del periodo agrupados por proveedor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer flex items-center gap-2 px-2 py-1.5 rounded-md border border-base-300/60 bg-base-200/40 text-xs">
            <input
              type="checkbox"
              className="toggle toggle-xs toggle-primary"
              checked={expandSplits}
              onChange={(e) => {
                setExpandSplits(e.target.checked);
                if (typeof window !== "undefined") {
                  localStorage.setItem("axorial.tx.expandSplits", e.target.checked ? "1" : "0");
                }
              }}
            />
            <span>Expandir sub-cuentas</span>
          </label>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn btn-primary btn-sm"
          >
            <Plus className="size-4" /> Nuevo proveedor
          </button>
        </div>
      </div>

      {topChart.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="card-elevated p-5 space-y-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
                Top proveedores · {period}
              </h3>
              <span className="text-xs text-base-content/55">
                Top {topChart.length} por monto gastado
              </span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(220, topChart.length * 32)}>
              <BarChart
                data={topChart}
                layout="vertical"
                margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
                <XAxis
                  type="number"
                  fontSize={11}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <YAxis dataKey="name" type="category" fontSize={11} width={160} />
                <Tooltip formatter={(v) => (typeof v === "number" ? fmtMx(v) : String(v))} />
                <Bar dataKey="total" name="Gasto">
                  {topChart.map((entry, i) => (
                    <Cell key={entry.name} fill={supplierColor(entry.name, i)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card-elevated p-5 space-y-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
                Distribución · {period}
              </h3>
              <span className="text-xs text-base-content/55">% del gasto por proveedor</span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(220, topChart.length * 32)}>
              <RPieChart>
                <Pie
                  data={distributionChart}
                  dataKey="total"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="45%"
                  outerRadius="85%"
                  paddingAngle={2}
                >
                  {distributionChart.map((entry, i) => (
                    <Cell
                      key={entry.name}
                      fill={
                        entry.name === "Otros"
                          ? "oklch(0.7 0.02 280)"
                          : supplierColor(entry.name, i)
                      }
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => (typeof v === "number" ? fmtMx(v) : String(v))} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ fontSize: 11 }}
                />
              </RPieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {unTagged.length > 0 && (
        <div className="card-elevated p-4 border-l-4 border-warning flex items-center gap-3">
          <div className="flex-1">
            <div className="text-sm font-medium">
              {unTagged.length} gastos sin proveedor en {period} ·{" "}
              <span className="text-error font-bold">{fmtMx(unTaggedTotal)}</span>
            </div>
            <div className="text-xs text-base-content/55">
              Liga estos gastos a un proveedor desde la pestaña Transacciones.
            </div>
          </div>
        </div>
      )}

      <div className="card-elevated p-3">
        <label className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded-md bg-base-200/50">
          <Search className="size-4 text-base-content/55" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar proveedor..."
            className="bg-transparent flex-1 text-sm outline-none"
          />
        </label>
        {filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-base-content/55">
            {supplierList.length === 0
              ? 'Sin proveedores. Crea el primero con "Nuevo proveedor" o marca una empresa existente como proveedor.'
              : "Ningún proveedor coincide con tu búsqueda."}
          </div>
        ) : (
          <ul className="divide-y divide-base-300/40">
            {filtered.map((s) => {
              const totals = totalsByCompany.get(s.id);
              return (
                <li
                  key={s.id}
                  className="px-2 py-2.5 flex items-center gap-3 hover:bg-base-200/40 rounded-md group"
                >
                  <div className="size-9 rounded-lg bg-base-200 grid place-items-center shrink-0 text-base-content/65">
                    <Building2 className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-xs text-base-content/55 truncate">
                      {s.industry ?? "—"}
                      {s.website && (
                        <>
                          {" · "}
                          <a
                            href={s.website}
                            target="_blank"
                            rel="noreferrer"
                            className="text-info hover:underline"
                          >
                            <ExternalLink className="size-3 inline" /> sitio
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className={clsx(
                        "text-sm font-bold tabular-nums",
                        totals ? "text-error" : "text-base-content/35",
                      )}
                    >
                      {totals ? fmtMx(totals.totalCents / 100) : "—"}
                    </div>
                    <div className="text-[11px] text-base-content/45">
                      {totals ? `${totals.count} tx en ${period}` : "sin gastos este mes"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing(s)}
                    className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Editar"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {creating && <SupplierModal mode="create" onClose={() => setCreating(false)} />}
      {editing && <SupplierModal mode="edit" company={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SupplierModal({
  mode,
  company,
  onClose,
}: {
  mode: "create" | "edit";
  company?: Company;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(company?.name ?? "");
  const [industry, setIndustry] = useState(company?.industry ?? "");
  const [website, setWebsite] = useState(company?.website ?? "");
  const [notes, setNotes] = useState(company?.notes ?? "");
  const [isSupplier, setIsSupplier] = useState(company?.isSupplier ?? true);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        industry: industry || undefined,
        website: website || undefined,
        notes: notes || undefined,
        isSupplier,
      };
      return mode === "create"
        ? api.post("/companies", body)
        : api.patch(`/companies/${company!.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      onClose();
    },
  });

  return (
    <div className="modal modal-open" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box max-w-md">
        <h3 className="font-display font-semibold text-lg mb-4">
          {mode === "create" ? "Nuevo proveedor" : "Editar proveedor"}
        </h3>
        <div className="space-y-3">
          <label className="form-control block">
            <div className="label py-1">
              <span className="label-text text-sm font-medium">Nombre</span>
            </div>
            <input
              className="input input-bordered w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="ej. Google Cloud · Adobe"
            />
          </label>
          <label className="form-control block">
            <div className="label py-1">
              <span className="label-text text-sm font-medium">Industria / categoría</span>
            </div>
            <input
              className="input input-bordered w-full"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="SaaS · Hosting · Logística..."
            />
          </label>
          <label className="form-control block">
            <div className="label py-1">
              <span className="label-text text-sm font-medium">Sitio web</span>
            </div>
            <input
              type="url"
              className="input input-bordered w-full"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://..."
            />
          </label>
          <label className="form-control block">
            <div className="label py-1">
              <span className="label-text text-sm font-medium">Notas</span>
            </div>
            <textarea
              rows={3}
              className="textarea textarea-bordered w-full"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="contacto, plan, contratos..."
            />
          </label>
          <label className="cursor-pointer label justify-start gap-3">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={isSupplier}
              onChange={(e) => setIsSupplier(e.target.checked)}
            />
            <span className="label-text text-sm">Es proveedor</span>
          </label>
        </div>
        {save.isError && (
          <div className="alert alert-error text-sm mt-3">
            {save.error instanceof Error ? save.error.message : "Error"}
          </div>
        )}
        <div className="modal-action mt-4">
          <button type="button" onClick={onClose} className="btn btn-ghost">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!name.trim() || save.isPending}
            className="btn btn-primary"
          >
            {save.isPending && <span className="loading loading-spinner loading-xs" />}
            {mode === "create" ? "Crear" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
